import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Gift } from "lucide-react";

/**
 * Parent-facing Bring a Friend promotion notice. Renders only when an active
 * referral promotion exists for this class — the admin toggle on
 * /admin/referrals is the single switch that shows or hides this copy.
 */
interface Props {
  classId: string;
  /** Optional session labels shown in the form; used to name the session. */
  sessionLabels?: string[];
}

export function BringAFriendNotice({ classId, sessionLabels }: Props) {
  const [promo, setPromo] = useState<{
    canonical_session_label: string;
    discount_percent: number;
    max_discount_cents: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("referral_promotions")
        .select("class_id, class_slug, canonical_session_label, discount_percent, max_discount_cents, active")
        .eq("active", true);
      if (cancelled) return;
      const rows = (data ?? []) as any[];
      if (rows.length === 0) return;
      // The public class card passes a UUID; promotions are keyed by slug.
      let slug: string | null = null;
      if (/^[0-9a-f-]{36}$/i.test(classId)) {
        const { data: cls } = await supabase
          .from("classes")
          .select("slug")
          .eq("id", classId)
          .maybeSingle();
        slug = (cls as any)?.slug ?? null;
      }
      if (cancelled) return;
      const bare = classId.replace(/^db:/, "");
      const keys = [classId, bare, slug].filter(Boolean);
      const match = rows.find(
        (r) => keys.includes(r.class_id) || keys.includes(r.class_slug),
      );
      if (!match) return;

      if (
        sessionLabels?.length &&
        !sessionLabels.some((l) => l === match.canonical_session_label)
      ) {
        return;
      }
      setPromo(match);
    })();
    return () => {
      cancelled = true;
    };
  }, [classId, sessionLabels?.join("|")]);

  if (!promo) return null;

  const cap = Math.round(promo.max_discount_cents / 100);

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-1.5">
      <p className="flex items-center gap-1.5 font-body font-bold text-foreground">
        <Gift size={16} className="text-primary" /> Bring a Friend &amp; Save
      </p>
      <p className="text-sm text-muted-foreground">
        Already joining this {promo.canonical_session_label} session? Share your personal
        referral code with another homeschool family. When they register using your code,
        they&apos;ll receive {Number(promo.discount_percent)}% off their registration, up to $
        {cap}.
      </p>
      <p className="text-xs text-muted-foreground">
        Both children need their own registration so each seat is properly reserved. Referral
        codes may be used once. The referred family may be new or returning, but must be a
        different household. Referral discounts can&apos;t be combined with other promotional
        discounts.
      </p>
    </div>
  );
}

export default BringAFriendNotice;
